import type { EPrivacyStatus } from "app/components/website-header/WebsiteHeaderReact";
import { useSiteInfo } from "app/services/sitesService/hooks/useSiteInfo";
import { getSiteDataFromResponse } from "app/services/sitesService/sitesUtils";

interface IGoogleAnalytics {
    isGADataLoading: boolean;
    hasGAToken: boolean;
    gaPrivacyStatus: EPrivacyStatus;
    gaLargeImage: string;
    gaIcon: string;
    gaCategory: string;
    isGAVirtual: boolean;
    gaDescription: string;
    refreshGAData: () => Promise<boolean>;
}

export const useGoogleAnalytics = (
    domainName: string,
    mainDomainOnly: boolean = true,
    includeCrossData: boolean = false,
): IGoogleAnalytics => {
    const { data, isLoading, refetch } = useSiteInfo(
        {
            keys: domainName,
            mainDomainOnly,
            includeCrossData,
        },
        {
            headers: {
                "Cache-Control": "no-cache",
            },
        },
        {
            enabled: !!domainName,
        },
    );

    const siteData = domainName && data ? getSiteDataFromResponse(data, domainName) : undefined;

    const refreshGAData = async (): Promise<boolean> => {
        if (!domainName) {
            return false;
        }
        const result = await refetch();
        const refreshedData = result.data && domainName ? getSiteDataFromResponse(result.data, domainName) : undefined;
        return !!refreshedData?.hasGaToken;
    };

    return {
        isGADataLoading: isLoading,
        hasGAToken: !!siteData?.hasGaToken,
        gaPrivacyStatus: siteData?.privacyStatus as EPrivacyStatus,
        gaLargeImage: siteData?.imageLarge || "",
        gaIcon: siteData?.icon || "",
        gaCategory: siteData?.category || "",
        isGAVirtual: !!siteData?.isVirtual,
        gaDescription: siteData?.description || "",
        refreshGAData,
    };
};
